<template>
  <section class="merchant-dashboard">
    <div class="table-tools d-flex align-items-center justify-content-between flex-column flex-md-row">
      <b-col
        md="6"
        sm="12"
        class="px-0">
        <b-col
          md="8"
          sm="12"
          class="float-left px-0 mb-3">
          <b-input-group class="search-filter">
            <b-input
              v-model="searchQuery"
              type="text"
              size="sm"
              autocomplete="off"
              placeholder="Search contacts..."
              @change="searchContacts"/>
            <b-input-group-text
              slot="append"
              @click="searchContacts">
              <img
                src="~@/assets/img/search-icon-light.svg"
                alt="Search"
                width="14px"
                height="14px">
            </b-input-group-text>
          </b-input-group>
        </b-col>
        <b-col
          md="4"
          sm="12"
          class="float-left px-0 mb-3 px-md-3">
          <b-dropdown
            :disabled="!selectedContacts.length || exportRunning"
            toggle-class="actions-toggle"
            menu-class="actions-menu"
            right
            no-flip
            class="dashboard-actions">
            <template slot="button-content">
              Actions
            </template>
            <b-dropdown-item @click="showDeleteModal">
              Delete
            </b-dropdown-item>
            <b-dropdown-item @click="startExport">
              Export As Csv
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
      </b-col>
      <b-col
        md="6"
        sm="12"
        class="px-0 mb-3 right-table-actions">
        <!-- Import button -->
        <b-button
          v-if="hasImportPermission && !pageSelected && !allSelected && !selectedContacts.length"
          class="action-button"
          @click="showImportModal()">Import</b-button>
        <!-- Filter button -->
        <b-dropdown
          v-if="!pageSelected && !allSelected && !selectedContacts.length"
          :clearable="false"
          toggle-class="actions-toggle action-button"
          right
          no-flip
          class="dashboard-actions">
          <template slot="button-content">
            {{ selectedTags.length ? selectedTagsName : 'Filter' }}
          </template>
          <b-dropdown-item
            :class="{active: !selectedTags.length}"
            @click="deselectTags()">All Contacts</b-dropdown-item>
          <b-dropdown-item
            v-for="tag in contactTags"
            :key="tag.id"
            class="tag-menu-item"
            @click="selectTag(tag)">
            {{ tag.name }}
            <b-icon-check
              v-if="selectedTags.includes(tag.id)"
              class="marked-tag"
              variant="info"/>
          </b-dropdown-item>
        </b-dropdown>

        <!-- Option to select all contacts on all pages -->
        <span v-if="selectedContacts.length || allSelected">{{ allSelected ? contacts.meta.total :selectedContacts.length }} selected</span>
        <b-button
          v-if="selectedContacts.length"
          class="action-button"
          @click="selectAllPagesToggle()">
          {{ allSelected ? 'Clear selection' : `Select all ${contacts.meta.total} contacts` }}
        </b-button>
      </b-col>
    </div>

    <div v-if="exportsWaiting">
      <div
        v-for="(exportLink, index) in exportsWaiting.slice(0, 2)"
        :key="index"
        class="sb-alert info">
        <span class="content">Your latest contact export is waiting to be downloaded!</span>
        <b-button
          class="sb-btn transparent danger"
          @click="removeFromExportList(exportLink)">
          Remove</b-button>
        <a
          :href="exportLink"
          target="_blank"
          class="btn sb-btn"
          @click="removeFromExportList(exportLink)">
          Download now
        </a>
      </div>
      <b-collapse
        id="collapse-exports"
        v-model="exportsExpanded">
        <div
          v-for="(exportLink, index) in exportsWaiting.slice(2)"
          :key="index"
          class="sb-alert info">
          <span class="content">Your latest contact export is waiting to be downloaded!</span>
          <b-button
            class="sb-btn transparent danger"
            @click="removeFromExportList(exportLink)">
            Remove</b-button>
          <a
            :href="exportLink"
            target="_blank"
            class="btn sb-btn"
            @click="removeFromExportList(exportLink)">
            Download now
          </a>
        </div>
      </b-collapse>
      <b-button
        v-if="exportsWaiting.length > 2"
        class="sb-btn transparent info mb-5"
        @click="exportsExpanded = !exportsExpanded">
        {{ exportsExpanded ? 'show less' : `${exportsWaiting.length - 2} more` }}
      </b-button>
    </div>

    <!-- Progress bar during contact import/export -->
    <div
      v-if="
        (importRunning || exportRunning || finalizeProgressBar || finalizeExportProgressBar) &&
          $refs.importModal && !$refs.importModal.isModalActive()"
      class="importing">
      <p class="text-center">{{ exportRunning || finalizeExportProgressBar ? 'Exporting' : 'Importing' }} contacts... ({{ exportRunning || finalizeExportProgressBar ? exportProgress : progress }}%)</p>
      <b-progress height="2px">
        <b-progress-bar
          variant="success"
          :value="exportRunning || finalizeExportProgressBar ? exportProgress : progress"/>
      </b-progress>
    </div>

    <!-- Contacts table -->
    <b-table
      :fields="fields"
      :items="contacts.data"
      show-empty
      responsive
      class="merchant-dashboard__table scroll-area-horizontal">
      <!-- Check column - HEAD -->
      <template v-slot:head(selectedContact)="">
        <b-check
          v-if="contacts.data.length"
          :value="true"
          :checked="pageSelected"
          @click.native.stop
          @change="selectAllContacts($event)"/>
      </template>

      <!-- Check column -->
      <template v-slot:cell(selectedContact)="data">
        <b-check
          :value="true"
          :checked="checkIfSelected(data.item.id)"
          @change="selectContact(data.item, $event)"/>
      </template>

      <!-- Phone column -->
      <template v-slot:cell(phone)="data">
        <router-link
          :to="{ name: 'ContactProfile', params: { contact_id: data.item.id }}"
          tag="div"
          class="table-link">{{ data.item.phone }}</router-link>
      </template>

      <!-- Tags column -->
      <template v-slot:cell(tags)="data">
        <span
          v-for="tag in data.value"
          :key="tag.id"
          class="tag-item mr-1">{{ tag.name }}</span>
      </template>

      <!-- Folder column -->
      <template v-slot:cell(conversation_status)="data">
        <span
          :class="setStatusClass(data.item.conversation_status)"
          class="tag-item">{{ data.item.conversation_status | removeUnderscore }}</span>
      </template>

      <!-- Status column -->
      <template v-slot:cell(status)="data">
        <span
          :class="setContactStatusClass(data.item.status)"
          class="tag-item">{{ data.item.status | removeUnderscore }}</span>
      </template>

      <!-- Created on column -->
      <template v-slot:cell(created_at)="data">
        {{ data.item.created_at | dateShort }}
      </template>

      <!-- Actions column - HEAD -->
      <template v-slot:head(actions)="data">
        {{ data.label }}
      </template>

      <!-- Actions column -->
      <template v-slot:cell(actions)="data">
        <b-dropdown
          no-caret
          no-flip
          right
          class="actions">
          <!-- button -->
          <template v-slot:button-content>
            <icon name="ellipsis-h"/>
          </template>

          <!-- items -->
          <b-dropdown-item
            :disabled="!data.item.contacted">
            <router-link
              :to="data.item.contacted ? {
                name: 'Chat',
                params : {
                  search_query: data.item.phone,
                  folder: data.item.conversation_status
                }
              } : ''"
              tag="span">
              Open Conversation
            </router-link>
          </b-dropdown-item>
          <b-dropdown-item @click="showNotificationModal(data.item)">
            Send Template Message
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <!-- No data content -->
      <template v-slot:empty="">
        <div
          v-if="!contacts.data.length && loadingContacts"
          class="loader">
          <div class="loading-circle"/>
        </div>
        <div
          v-if="!contacts.data.length && !loadingContacts"
          class="no-data text-center">
          <img
            src="~@/assets/img/no-data.svg"
            alt="No Data">
          <span
            v-if="searchQuery"
            class="d-block mt-2">
            We couldn't find anything for <b>{{ searchQuery }}</b>.</span>
          <span v-else>You don’t have any contacts at the moment.</span>
        </div>
      </template>
    </b-table>

    <div class="paging mb-5 clearfix">
      <p class="float-left pages">{{ contacts.meta.current_page }} of {{ contacts.meta.last_page }}</p>
      <button
        :disabled="page === contacts.meta.last_page"
        class="btn btn-default btn-paging float-right"
        @click="nextPage">Next</button>
      <button
        :disabled="page === 1"
        class="btn btn-default btn-paging float-right"
        @click="previousPage">Back</button>
      <b-input
        v-model="contacts.meta.current_page"
        :disabled="contacts.meta.last_page === 1"
        type="number"
        min="1"
        class="page-input"
        placeholder="1"
        @change="changePage"/>
    </div>
    <NotificationSendModal
      ref="notificationSendModal"
      :force-fill-subscriber="true"/>
    <DeleteModal
      v-if="selectedContacts.length && hasManageContactsPermission"
      ref="deleteModal"
      :contact-ids="selectedContacts"
      :delete-all="allSelected"
      :total-count="contacts.meta.total"
      @deleted="postDeleteAction"/>
    <InfoModal
      ref="infoModal"
      message="You don't have permission to do this action."/>
    <ImportModal
      ref="importModal"
      @close="refreshContacts"/>
  </section>
</template>

<style lang="scss">
.tag-menu-item {
  .dropdown-item {
    position: relative;
    padding: 10px 20px 10px 15px!important;
  }

  .marked-tag {
    position: absolute;
    right: 5px;
    top: 7px;
  }
}
</style>

<script>
import { mapGetters, mapActions } from 'vuex';
import NotificationSendModal from '../notifications/modals/NotificationSendModal';
import DeleteModal from './modals/DeleteModal';
import InfoModal from '../../common/modals/InfoModal';
import ImportModal from './modals/ImportModal';

export default {
  name: 'ContactsTable',
  components: {
    NotificationSendModal,
    DeleteModal,
    InfoModal,
    ImportModal,
  },
  data() {
    return {
      fields: [
        {
          key: 'selectedContact',
          label: '',
          thClass: 'checkbox-col',
          tdClass: 'checkbox-col',
        },
        {
          key: 'phone',
          label: 'Phone number',
        },
        'name',
        'tags',
        {
          key: 'conversation_status',
          label: 'Folder',
        },
        {
          key: 'status',
          label: 'Status',
        },
        {
          key: 'created_at',
          label: 'Created on',
          class: 'text-right',
        },
        {
          key: 'actions',
          class: 'text-right',
        },
      ],
      hasManageContactsPermission: this.$hasPermission('manage_contacts', this.$route.params.merchant_id),
      hasSendNotificationPermission: this.$hasPermission('send_notifications', this.$route.params.merchant_id),
      selectedContacts: [],
      selectedTags: [],
      searchQuery: '',
      page: 1,
      limit: 10,
      errorMessage: '',
      finalizeProgressBar: false,
      pageSelected: false,
      allSelected: false,
      finalizeExportProgressBar: false,
      exportsWaiting: [],
      exportsExpanded: false,
    };
  },
  computed: {
    ...mapGetters({
      contacts: 'contact/contactList',
      loadingContacts: 'contact/loadingContacts',
      contactTags: 'contact/contactTagList',
      apiError: 'contact/apiError',
      importStats: 'pusher/importStats',
      importRunning: 'pusher/importRunning',
      merchant: 'merchant/merchant',
      exportStats: 'pusher/exportStats',
      exportId: 'contact/exportId',
      exportError: 'contact/exportError',
      exportRunning: 'pusher/exportRunning',
    }),
    selectedTagsName() {
      const tagNames = this.contactTags.reduce((filtered, tag) => {
        if (this.selectedTags.includes(tag.id)) {
          filtered.push(tag.name);
        }

        return filtered;
      }, []);
      let buttonText = tagNames[0];
      if (tagNames.length > 1) {
        buttonText = `${tagNames.length} selected`;
      }
      return buttonText;
    },
    progress() {
      let progress = 0;
      if (this.importStats && this.importStats.added_contacts >= 0 && this.importStats.failed_contacts >= 0 && this.importStats.total_contacts > 0) {
        progress = (this.importStats.added_contacts + this.importStats.failed_contacts) / this.importStats.total_contacts * 100;
      }
      return progress.toFixed(0);
    },
    hasImportPermission() {
      return this.merchant.extra_features.some(feature => feature.key === 'import-contacts');
    },
    exportProgress() {
      let progress = 0;
      if (this.exportStats && this.exportStats.added_contacts >= 0 && this.exportStats.total_contacts > 0) {
        progress = this.exportStats.added_contacts / this.exportStats.total_contacts * 100;
      }
      return progress.toFixed(0);
    },
  },
  watch: {
    apiError(error) {
      this.errorMessage = error.data.message;
    },
    importRunning(state) {
      // when import is finished prevent instant hiding of the progress bar and allow one second for the progress bar to reach 100%
      if (!state) {
        this.finalizeProgressBar = true;
        setTimeout(() => {
          this.finalizeProgressBar = false;
        }, 1000);
      }
    },
    exportId(exportId) {
      if (exportId) {
        // this.subscribeToExportStats({
        //   exportUUID: this.exportId,
        //   merchantId: this.$route.params.merchant_id,
        // });
      }
    },
    exportRunning(state) {
      if (state) {
        this.clearSelection();
      } else {
        // when export is finished prevent instant hiding of the progress bar and allow one second for the progress bar to reach 100%
        this.finalizeExportProgressBar = true;
        setTimeout(() => {
          this.finalizeExportProgressBar = false;
        }, 1000);
      }
    },
  },
  mounted() {
    this.loadContacts();

    if (!this.contactTags.length) {
      this.fetchContactTags({
        merchantId: this.$route.params.merchant_id,
      });
    }

    this.isThereSomethingToDownload();

    this.$root.$on('bv::toast:hidden', () => { this.isThereSomethingToDownload(); });
  },
  methods: {
    ...mapActions({
      // subscribeToExportStats: 'pusher/subscribeToExportStats',
      fetchContacts: 'contact/fetchContacts',
      deleteContact: 'contact/deleteContact',
      fetchContactTags: 'contact/fetchContactTags',
      exportContacts: 'contact/exportContacts',
    }),
    whatsappUrl(phoneNumber) {
      const phone = phoneNumber.replace(/\D+/g, '');
      return `https://wa.me/${phone}`;
    },
    setStatusClass(status) {
      return {
        success: status === 'done',
        danger: status === 'spam',
      };
    },
    setContactStatusClass(status) {
      return {
        success: status === 'subscribed',
        danger: status === 'unsubscribed',
      };
    },
    selectTag(tag) {
      const tagIndex = this.selectedTags.indexOf(tag.id);
      if (tagIndex > -1) {
        this.selectedTags.splice(tagIndex, 1);
      } else {
        this.selectedTags.push(tag.id);
      }

      this.loadContacts();
    },
    deselectTags() {
      this.selectedTags = [];
      this.loadContacts();
    },
    checkIfSelected(contactId) {
      return this.selectedContacts.includes(contactId);
    },
    selectContact(contact, selected) {
      this.pageSelected = false;
      this.allSelected = false;
      if (selected) {
        this.selectedContacts = this.$lodash.union(this.selectedContacts, [contact.id]);
      } else {
        this.selectedContacts = this.$lodash.remove(this.selectedContacts, contactId => contactId !== contact.id);
      }
    },
    selectAllContacts(selected) {
      if (selected) {
        this.pageSelected = true;
        this.selectedContacts = this.contacts.data.map(contact => contact.id);
      } else {
        this.selectedContacts = [];
        this.pageSelected = false;
        this.allSelected = false;
      }
    },
    showNotificationModal(contact) {
      if (this.hasSendNotificationPermission) {
        this.$refs.notificationSendModal.setRecipients({ contacts: [contact] });
        this.$refs.notificationSendModal.show();
      } else {
        this.$infoModal.show({
          message: 'You don\'t have permission to do this action.',
        });
      }
    },
    searchContacts() {
      this.clearSelection();
      this.page = 1;
      this.loadContacts();
    },
    changePage(page) {
      page = parseInt(page, 10);
      if (page > this.contacts.meta.last_page) {
        page = this.contacts.meta.last_page;
      }
      if (!page || page < 1) {
        page = 1;
      }
      this.page = page;
      this.loadContacts();
    },
    nextPage() {
      if (this.page >= this.contacts.meta.last_page) {
        return;
      }
      this.page += 1;
      this.loadContacts();
    },
    previousPage() {
      if (this.page === 1) {
        return;
      }
      this.page -= 1;
      this.loadContacts();
    },
    loadContacts() {
      this.selectedContact = false;
      this.fetchContacts({
        merchant_id: this.$route.params.merchant_id,
        data: {
          limit: this.limit,
          page: this.page,
          search_query: this.searchQuery,
          tags: this.selectedTags.join(),
        },
      });
    },
    showDeleteModal() {
      if (this.hasManageContactsPermission) {
        this.$refs.deleteModal.show();
      } else {
        this.$refs.infoModal.show();
      }
    },
    postDeleteAction() {
      this.clearSelection();
      this.loadContacts();
    },
    showImportModal() {
      this.$refs.importModal.show();
    },
    selectAllPagesToggle() {
      if (this.allSelected) {
        this.clearSelection();
      } else {
        this.allSelected = true;
        this.selectedContacts = this.contacts.data.map(contact => contact.id);
        this.pageSelected = true;
      }
    },
    startExport() {
      if (!window.useMultiExport) {
        // clear exports waiting from the table info
        this.exportsWaiting = [];
        localStorage.removeItem(`exportWaiting.${this.$route.params.merchant_id}`);
        this.closeAllToasts();
      }
      const exportFilter = {
        contacts: this.allSelected ? '' : this.selectedContacts,
        search_query: this.searchQuery,
        tags: this.selectedTags.join(),
      };
      this.exportContacts({
        exportFilter,
        merchantId: this.$route.params.merchant_id,
      });
    },
    clearSelection() {
      this.selectedContacts = [];
      this.pageSelected = false;
      this.allSelected = false;
    },
    isThereSomethingToDownload() {
      // timeout needed for the toasts to be updated
      setTimeout(() => {
        const activeToasts = this.toastsActive();
        let exportLinks = localStorage.getItem(`exportWaiting.${this.$route.params.merchant_id}`);
        if (exportLinks) {
          exportLinks = exportLinks.split(',').filter((exportLink) => {
            const exportUUID = exportLink.substr(exportLink.lastIndexOf('/') + 1).replace('.csv', '');
            return !activeToasts.includes(exportUUID);
          });
        }
        this.exportsWaiting = exportLinks;
      }, 300);
    },
    refreshContacts() {
      this.page = 1;
      this.loadContacts();
    },
    toastsActive() {
      // eslint-disable-next-line no-underscore-dangle
      return window.sbToast._vm.$children
        .map(toast => toast.id)
        .filter(Boolean)
        .filter(toastId => toastId.toString().startsWith('export-toast-'))
        .map(toastId => toastId.replace('export-toast-', ''));
    },
    closeAllToasts() {
      const toastIds = this.toastsActive();
      toastIds.forEach((toastId) => {
        window.sbToast.hide(`export-toast-${toastId}`);
      });
    },
    removeFromExportList(fileDownloadUrl) {
      localStorage.setItem(`exportWaiting.${this.$route.params.merchant_id}`, localStorage.getItem(`exportWaiting.${this.$route.params.merchant_id}`).replace(new RegExp(`${fileDownloadUrl},?`, 'gi'), '').replace(/,$/, ''));
      this.isThereSomethingToDownload(fileDownloadUrl);
    },
  },
};
</script>
